import { useState, useEffect, useRef, useCallback, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './useAuth';
import { apiFetch } from '../lib/api';
import { secondStepPrompt, cleanCodeInput, codeComplete, type SecondStep } from './second-step';
import { RubyLogo } from './RubyLogo';
import { StagingBanner } from '../ui/StagingBanner';
import { OwlWait } from '@ruby/owl/react';
import { markSignedIn, takeSignedIn } from './signed-in-moment';

interface OAuthProviders {
  google: boolean;
  microsoft: boolean;
  apple: boolean;
}

/** The server's second-step answer: `codeSent` false means the screen must
 *  ask for the first code (after Google sign-in). */
type SecondStepAnswer = SecondStep & { requires2FA?: boolean; codeSent?: boolean };

export function LoginPage() {
  const { login, verifyOtp } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState<OAuthProviders | null>(null);

  // 2FA state
  const [twoFactor, setTwoFactor] = useState<SecondStep | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  // A texted code can keep the password step waiting ~12 s on voip.ms.
  const [slowSignIn, setSlowSignIn] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  /** A fresh code, from the pending sign-in alone — no password re-post. */
  const sendCode = useCallback(async () => {
    setError('');
    setOtpCode('');
    setSendingCode(true);
    try {
      const step = await apiFetch<SecondStepAnswer>('/api/auth/resend-2fa', { method: 'POST' });
      setTwoFactor({ method: step.method, hint: step.hint, notice: step.notice });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setSendingCode(false);
    }
  }, []);

  // A second step already in progress: a reload of the code screen, or Google
  // sign-in for a 2FA account, which lands here owing its first code.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/pending-2fa')
      .then((r) => (r.ok ? r.json() as Promise<SecondStepAnswer> : null))
      .then((step) => {
        if (cancelled || !step?.requires2FA) return;
        setTwoFactor({ method: step.method, hint: step.hint, notice: step.notice });
        if (!step.codeSent) void sendCode();
      })
      .catch(() => { /* no sign-in in progress */ });
    return () => { cancelled = true; };
  }, [sendCode]);

  useEffect(() => {
    if (!submitting) { setSlowSignIn(false); return; }
    const timer = setTimeout(() => setSlowSignIn(true), 3000);
    return () => clearTimeout(timer);
  }, [submitting]);

  // Check for OAuth error in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (oauthError) {
      setError(decodeURIComponent(oauthError).replace(/_/g, ' '));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch available OAuth providers
  useEffect(() => {
    fetch('/api/auth/oauth/providers')
      .then((r) => r.json())
      .then((data) => setProviders(data))
      .catch(() => setProviders({ google: false, microsoft: false, apple: false }));
  }, []);

  // Auto-focus OTP input when 2FA view is shown
  useEffect(() => {
    if (twoFactor) otpInputRef.current?.focus();
  }, [twoFactor]);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      markSignedIn(); // before the call: the portal can open as it returns
      const result = await login(username, password);
      if (result.requires2FA) {
        takeSignedIn(); // not signed in yet: the code step sets it again
        const step = result as unknown as SecondStepAnswer;
        setTwoFactor({ method: step.method, hint: step.hint, notice: step.notice });
      }
    } catch (err: unknown) {
      takeSignedIn(); // not signed in after all
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      markSignedIn(); // read by the sidebar's gem (SignInGem)
      await verifyOtp(otpCode);
    } catch (err: unknown) {
      takeSignedIn(); // not signed in after all
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    // Ends the second step on the server too, or a reload would bring it back.
    void apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setTwoFactor(null);
    setOtpCode('');
    setError('');
  };

  const hasSocialLogin = providers && (providers.google || providers.microsoft || providers.apple);

  // ---- 2FA verification view ----
  if (twoFactor) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <StagingBanner />
        <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex flex-col items-center mb-6">
              {/* While a code is on its way, the owl waits by its phone in
                  the logo's own slot, so nothing moves. */}
              <div className="h-10 mb-4 flex items-center justify-center">
                {sendingCode ? <OwlWait kind="phone" size={40} /> : <RubyLogo className="h-10" />}
              </div>
              <h1 className="text-xl font-bold text-gray-900">Verification Required</h1>
              <p data-testid="second-step-prompt" className="text-sm text-gray-500 mt-2 text-center">
                {secondStepPrompt(twoFactor, sendingCode)}
              </p>
            </div>

            {error && (
              <div data-testid="auth-error" className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div>
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode={twoFactor.method === 'totp' ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  maxLength={twoFactor.method === 'totp' ? 10 : 6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(cleanCodeInput(twoFactor.method, e.target.value))}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center text-2xl tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="------"
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !codeComplete(twoFactor.method, otpCode)}
                className="w-full py-2.5 px-4 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Verifying...' : 'Verify'}
              </button>
            </form>

            <div className="mt-4 flex items-center justify-between text-sm">
              <button onClick={handleBack} className="text-gray-500 hover:text-gray-700">
                Back to login
              </button>
              {twoFactor.method !== 'totp' && (
                <button onClick={() => void sendCode()} disabled={submitting || sendingCode} className="text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                  {sendingCode ? 'Sending…' : 'Resend code'}
                </button>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // ---- Normal login view ----
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <StagingBanner />
      <div className="flex-1 flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {/* Branding */}
          <div className="flex flex-col items-center mb-8">
            {/* A texted code keeps "Sign in" waiting ~12 s: from the 3 s mark
                the owl waits by its phone in the logo's slot. */}
            <div className="h-14 mb-3 flex items-center justify-center">
              {submitting && slowSignIn ? <OwlWait kind="phone" size={56} /> : <RubyLogo className="h-14" />}
            </div>
            <p className="text-sm text-gray-500">Sign in to your account</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Social login buttons */}
          {hasSocialLogin && (
            <>
              <div className="space-y-3 mb-6">
                {providers.google && (
                  <a
                    href="/api/auth/oauth/google"
                    className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <GoogleIcon />
                    Sign in with Google
                  </a>
                )}
                {providers.microsoft && (
                  <a
                    href="/api/auth/oauth/microsoft"
                    className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <MicrosoftIcon />
                    Sign in with Microsoft
                  </a>
                )}
                {providers.apple && (
                  <a
                    href="/api/auth/oauth/apple"
                    className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                  >
                    <AppleIcon />
                    Sign in with Apple
                  </a>
                )}
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-400">or</span>
                </div>
              </div>
            </>
          )}

          {/* Password form */}
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username or email
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Username or email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs text-indigo-600 hover:text-indigo-800">
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 px-4 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (slowSignIn ? 'Sending your code…' : 'Signing in...') : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.1 13.28c-.3.68-.65 1.3-1.06 1.87-.56.78-1.02 1.32-1.37 1.62-.55.5-1.13.75-1.76.77-.45 0-.99-.13-1.62-.39-.63-.26-1.21-.39-1.74-.39-.56 0-1.15.13-1.79.39-.64.26-1.15.4-1.55.41-.6.03-1.2-.24-1.78-.79-.38-.33-.86-.89-1.42-1.7-.6-.85-1.1-1.84-1.48-2.97C.18 10.7 0 9.33 0 8.02c0-1.5.32-2.79.97-3.87a5.7 5.7 0 012.05-2.06A5.51 5.51 0 015.78 1.2c.48 0 1.1.15 1.88.44.77.29 1.27.44 1.49.44.16 0 .72-.18 1.65-.53.89-.33 1.63-.47 2.24-.42 1.66.13 2.9.79 3.72 1.97-1.48.9-2.21 2.16-2.19 3.78.02 1.26.47 2.31 1.35 3.14.4.38.85.67 1.34.88-.11.31-.22.61-.34.9zM11.53.37c0 .99-.36 1.91-1.08 2.78-.86 1.01-1.9 1.6-3.04 1.5a3.05 3.05 0 01-.02-.37c0-.95.41-1.96 1.14-2.79.36-.42.83-.76 1.39-1.04.56-.27 1.09-.42 1.58-.45.02.12.03.25.03.37z" fill="currentColor" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
